import { useRef, useState } from "react";
import { cn } from "@/lib/utils";
import {
  DEFAULT_HUB_REACTION,
  HUB_REACTIONS,
  HUB_REACTION_BY_TYPE,
  hubPresentReactions,
  hubReactionTotal,
  type HubReactionCounts,
  type HubReactionType,
} from "@/lib/hub/reactions";

/**
 * LR HUB reaction control — Facebook-style: quick tap = 10-4 (default),
 * hover (desktop) / long-press (mobile) opens the 5-image picker.
 *
 * Icons are the uploaded transparent PNG pack served from the CDN
 * (see src/lib/hub/reactions.ts). No lucide/generated substitutes.
 */

function Icon({
  type,
  size = 22,
  className,
}: {
  type: HubReactionType;
  size?: number;
  className?: string;
}) {
  const def = HUB_REACTION_BY_TYPE[type];
  return (
    <span
      className="inline-flex flex-none items-center justify-center"
      style={{ width: size, height: size, minWidth: size, minHeight: size }}
    >
      <img
        src={def.src}
        alt={def.label}
        loading="eager"
        decoding="sync"
        draggable={false}
        width={size}
        height={size}
        className={cn("block h-full w-full max-w-none select-none object-contain", className)}
      />
    </span>
  );
}


/** Small summary row: the icons present on the post + the total count. */
export function HubReactionSummary({ counts }: { counts: HubReactionCounts }) {
  const present = hubPresentReactions(counts);
  const total = hubReactionTotal(counts);
  if (!total) return null;
  return (
    <div className="flex items-center gap-1.5">
      <div className="flex items-center -space-x-1">
        {present.slice(0, 5).map((r) => (
          <span
            key={r.type}
            title={`${r.label} · ${counts[r.type]}`}
            className="inline-flex h-[24px] w-[24px] flex-none items-center justify-center rounded-full bg-card ring-1 ring-border"
          >
            <Icon type={r.type} size={20} />

          </span>
        ))}
      </div>
      <span className="text-[11px] font-semibold text-muted-foreground">· {total}</span>
    </div>
  );
}

export function HubReactionButton({
  mine,
  disabled,
  onReact,
}: {
  mine: HubReactionType | null;
  disabled?: boolean;
  onReact: (type: HubReactionType) => void;
}) {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<number | null>(null);
  const pressTimer = useRef<number | null>(null);

  function openNow() {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    setOpen(true);
  }
  function closeSoon() {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setOpen(false), 180);
  }

  const active = mine ? HUB_REACTION_BY_TYPE[mine] : null;

  return (
    <div
      className="relative"
      onMouseEnter={openNow}
      onMouseLeave={closeSoon}
      onTouchStart={() => {
        pressTimer.current = window.setTimeout(() => setOpen(true), 350);
      }}
      onTouchEnd={() => {
        if (pressTimer.current) window.clearTimeout(pressTimer.current);
      }}
    >
      {open ? (
        <div
          className="absolute bottom-full left-0 z-20 mb-2 flex w-max animate-fade-in items-center gap-1 whitespace-nowrap rounded-full border border-border bg-card px-2 py-1.5"
          style={{ boxShadow: "var(--shadow-card)" }}
        >
          {HUB_REACTIONS.map((r) => (
            <button
              key={r.type}
              type="button"
              title={r.label}
              aria-label={r.label}
              disabled={disabled}
              onClick={() => {
                onReact(r.type);
                setOpen(false);
              }}
              className={cn(
                "flex-none rounded-full p-1 transition-transform hover:scale-110",
                mine === r.type ? "bg-brand-teal-subtle" : "hover:bg-muted",
              )}
            >
              <Icon type={r.type} size={30} />
            </button>
          ))}

        </div>
      ) : null}

      <button
        type="button"
        disabled={disabled}
        onClick={() => onReact(mine ?? DEFAULT_HUB_REACTION)}
        className={cn(
          "inline-flex items-center gap-1.5 rounded-md px-1.5 py-1 text-[11.5px] font-semibold transition-colors",
          active
            ? "text-brand-teal-deep"
            : "text-muted-foreground hover:text-brand-teal-deep",
        )}
      >
        <Icon type={mine ?? DEFAULT_HUB_REACTION} size={20} className={active ? "" : "opacity-70"} />
        {active ? active.label : "React"}
      </button>
    </div>
  );
}

export default HubReactionButton;
